import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import UserContext from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import authenticate from "../../utils/Authenticate";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { isAdminRole } from "../../utils/roles";
import { n2, normalizeUnit } from "../../utils/fmt";
import { currencySymbol } from "../../utils/currency";

const ZoneMenuCreation = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [hasGeneratedRawMaterials, setHasGeneratedRawMaterials] =
    useState(false);

  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState("");
  const [menuDetails, setMenuDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dishQuantities, setDishQuantities] = useState({});
  const [selectedMenus, setSelectedMenus] = useState([
    { id: "", details: null, quantities: {} },
  ]);
  const [menuYieldApplied, setMenuYieldApplied] = useState({}); // track which menu indices have central yield applied
  const [menuYieldSaveStatus, setMenuYieldSaveStatus] = useState({}); // { index: 'success' | 'error' }
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [totalThaal, setTotalThaal] = useState(0);

  // New state for raw materials management
  const [rawMaterials, setRawMaterials] = useState([]);
  const [showRawMaterials, setShowRawMaterials] = useState(false);
  
  // Updated state for individual dish add item functionality
  const [showAddItemForDish, setShowAddItemForDish] = useState({}); // Track which dish's add item is open
  const [searchTerms, setSearchTerms] = useState({}); // Track search terms for each dish
  const [searchResults, setSearchResults] = useState({});
  const [searchLoading, setSearchLoading] = useState({});
  const [submittedFormData, setSubmittedFormData] = useState([]);
  
  // New states for add item form - now tracked per dish
  const [selectedNewItems, setSelectedNewItems] = useState({});
  const [newItemQuantities, setNewItemQuantities] = useState({});

  function mapItemsFromMultipleMenus(menusArray) {
    const itemMap = {};

    if (!menusArray) return [];

    for (const menu of menusArray) {
        if (!menu?.dishes) continue;

        for (const dish of menu.dishes) {
            for (const itemEntry of dish.item) {
                const item = itemEntry.dish;
                const quantity = itemEntry.quantity || 0;

                if (!item || !item._id) continue;

                if (!itemMap[item._id]) {
                    itemMap[item._id] = {
                        itemId: item._id,
                        itemName: item.name,
                        quantity: quantity,
                        unit: item.unit,
                        pricePerUnit: item.basePrice || 0,
                        searchTerm: item.name,
                        selectedItem: {
                          basePrice: item.basePrice || 0,
                          category: item.category || 'item',
                          createdAt: item.createdAt,
                          name: item.name,
                          purchaseType: item.purchaseType || 'Supplier',
                          unit: item.unit,
                          updatedAt: item.updatedAt || "2024-06-08T16:36:10.841Z",
                          __v: item._v,
                          _id: item._id
                        }
                    };
                } else {
                    itemMap[item._id].quantity += quantity;
                }
            }
        }
    }
    

    return Object.values(itemMap);
}

const handleExportConsolidatedExcel = () => {
  const COLS = 4;
  const consolidatedMaterials = mapItemsFromMultipleMenus(rawMaterials);

  const BORDER = {
    top:    { style: "thin", color: { rgb: "555555" } },
    bottom: { style: "thin", color: { rgb: "555555" } },
    left:   { style: "thin", color: { rgb: "555555" } },
    right:  { style: "thin", color: { rgb: "555555" } },
  };
  const sc = (ws, r, c, s) => {
    const ref = XLSX.utils.encode_cell({ r, c });
    if (!ws[ref]) ws[ref] = { v: "", t: "s" };
    ws[ref].s = s;
  };
  const scRow = (ws, r, s) => { for (let c = 0; c < COLS; c++) sc(ws, r, c, s); };

  const S = {
    title:   { font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "1F4E3D" } }, alignment: { horizontal: "center", vertical: "center" }, border: BORDER },
    colHdr:  { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "343A40" } }, alignment: { horizontal: "center" }, border: BORDER },
    dataEven:{ font: {}, fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } }, border: BORDER },
    dataOdd: { font: {}, fill: { patternType: "solid", fgColor: { rgb: "F0FDF4" } }, border: BORDER },
    dataNum: (odd) => ({ font: {}, fill: { patternType: "solid", fgColor: { rgb: odd ? "F0FDF4" : "FFFFFF" } }, alignment: { horizontal: "right" }, border: BORDER }),
  };

  const wsData = [];
  let ri = 0;

  wsData.push(["Consolidated Raw Materials", "", "", ""]); const titleR = ri++;
  wsData.push([]); ri++;
  wsData.push(["Material Name", "Total Quantity", "Unit", `Price / Unit (${currencySymbol(user)})`]); const hdrR = ri++;

  const dataStart = ri;
  consolidatedMaterials.forEach((item) => {
    wsData.push([item.itemName, n2(item.quantity), normalizeUnit(item.unit), n2(item.pricePerUnit)]);
    ri++;
  });

  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws["!merges"] = [{ s: { r: titleR, c: 0 }, e: { r: titleR, c: COLS - 1 } }];
  ws["!cols"] = [{ wch: 34 }, { wch: 16 }, { wch: 10 }, { wch: 20 }];

  scRow(ws, titleR, S.title);
  scRow(ws, hdrR, S.colHdr);
  for (let r = dataStart; r < dataStart + consolidatedMaterials.length; r++) {
    const odd = (r - dataStart) % 2 !== 0;
    sc(ws, r, 0, odd ? S.dataOdd : S.dataEven);
    sc(ws, r, 1, S.dataNum(odd));
    sc(ws, r, 2, odd ? S.dataOdd : S.dataEven);
    sc(ws, r, 3, S.dataNum(odd));
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Consolidated Materials");
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
  saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" }), "Consolidated_Raw_Materials.xlsx");
};


  const handleExportToExcel = () => {
    // 7 columns: [0-2] left menu | [3] gap | [4-6] right menu
    const MAX_COL = 6;

    const BORDER = {
      top:    { style: "thin", color: { rgb: "555555" } },
      bottom: { style: "thin", color: { rgb: "555555" } },
      left:   { style: "thin", color: { rgb: "555555" } },
      right:  { style: "thin", color: { rgb: "555555" } },
    };

    // ── cell map helpers ──────────────────────────────────────────────────
    const cells = {};
    const merges = [];
    let maxRow = 0;

    const setCell = (r, c, v, t, s) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      cells[addr] = { v, t: t || (typeof v === "number" ? "n" : "s") };
      if (s) cells[addr].s = s;
      if (r > maxRow) maxRow = r;
    };

    const fillEmpty = (r, c) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!cells[addr]) cells[addr] = { v: "", t: "s" };
      if (r > maxRow) maxRow = r;
    };

    const mergeRange = (r1, c1, r2, c2) => {
      merges.push({ s: { r: r1, c: c1 }, e: { r: r2, c: c2 } });
      for (let r = r1; r <= r2; r++)
        for (let c = c1; c <= c2; c++)
          if (!(r === r1 && c === c1)) fillEmpty(r, c);
    };

    const applyStyle = (r, c, s) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!cells[addr]) cells[addr] = { v: "", t: "s" };
      cells[addr].s = s;
    };

    const styleRange = (r1, c1, r2, c2, s) => {
      for (let r = r1; r <= r2; r++)
        for (let c = c1; c <= c2; c++)
          applyStyle(r, c, s);
    };

    // ── styles ────────────────────────────────────────────────────────────
    const S = {
      title:    { font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "1F4E3D" } }, alignment: { horizontal: "center", vertical: "center" }, border: BORDER },
      summMenu: { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "2D6A4F" } }, border: BORDER },
      summDish: { font: { color: { rgb: "065F46" } }, fill: { patternType: "solid", fgColor: { rgb: "D1FAE5" } }, border: BORDER },
      thaal:    { font: { bold: true, color: { rgb: "065F46" } }, fill: { patternType: "solid", fgColor: { rgb: "A7F3D0" } }, alignment: { horizontal: "center" }, border: BORDER },
      colHdr:   { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "343A40" } }, alignment: { horizontal: "center" }, border: BORDER },
      menuGrp:  { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { patternType: "solid", fgColor: { rgb: "40916C" } }, border: BORDER },
      dishGrp:  { font: { bold: true, italic: true, color: { rgb: "1B4332" } }, fill: { patternType: "solid", fgColor: { rgb: "95D5B2" } }, border: BORDER },
      dataEven: { font: {}, fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } }, border: BORDER },
      dataOdd:  { font: {}, fill: { patternType: "solid", fgColor: { rgb: "F0FDF4" } }, border: BORDER },
      dataNum:  (odd) => ({ font: {}, fill: { patternType: "solid", fgColor: { rgb: odd ? "F0FDF4" : "FFFFFF" } }, alignment: { horizontal: "right" }, border: BORDER }),
      gap:      { fill: { patternType: "solid", fgColor: { rgb: "EEEEEE" } } },
    };

    let ri = 0;

    // ── Order Summary (spans all 7 cols) ──────────────────────────────────
    setCell(ri, 0, "Order Summary", "s", S.title);
    mergeRange(ri, 0, ri, MAX_COL);
    styleRange(ri, 0, ri, MAX_COL, S.title);
    ri++;

    ri++; // blank

    submittedFormData.forEach((menuBlock, idx) => {
      if (idx > 0) ri++;
      setCell(ri, 0, `Menu: ${menuBlock.menuName}`, "s", S.summMenu);
      mergeRange(ri, 0, ri, MAX_COL);
      styleRange(ri, 0, ri, MAX_COL, S.summMenu);
      ri++;

      menuBlock.dishes.forEach((dish) => {
        const perThaal = dish.perThaalQuantity != null && dish.perThaalQuantity !== "" ? n2(dish.perThaalQuantity) : "—";
        setCell(ri, 0, `  ${dish.dishName}`, "s", S.summDish);
        setCell(ri, 1, `${n2(dish.quantity)} ${normalizeUnit(dish.unit)}`, "s", S.summDish);
        setCell(ri, 2, `Per Thaal: ${perThaal}`, "s", S.summDish);
        mergeRange(ri, 2, ri, MAX_COL);
        styleRange(ri, 0, ri, MAX_COL, S.summDish);
        ri++;
      });
    });

    ri++;
    setCell(ri, 0, `Total Thaal: ${totalThaal}`, "s", S.thaal);
    mergeRange(ri, 0, ri, MAX_COL);
    styleRange(ri, 0, ri, MAX_COL, S.thaal);
    ri++;

    ri++; // blank
    ri++; // blank

    // ── Raw Materials — side by side (2 menus per row) ────────────────────
    const buildMenuSeq = (menuBlock) => {
      const seq = [];
      seq.push({ type: "menuHdr", text: menuBlock.menu });
      seq.push({ type: "colHdr" });
      let dataIdx = 0;
      menuBlock.dishes.forEach((dishBlock) => {
        seq.push({ type: "dishHdr", text: dishBlock.dish });
        dishBlock.item.forEach((mat) => {
          seq.push({ type: "data", name: mat.dish.name, qty: n2(mat.quantity), unit: normalizeUnit(mat.dish.unit), dataIdx: dataIdx++ });
        });
      });
      return seq;
    };

    const placeSeq = (seq, colBase, baseRow) => {
      const C0 = colBase, C1 = colBase + 1, C2 = colBase + 2;
      seq.forEach((item, i) => {
        const r = baseRow + i;
        if (item.type === "menuHdr") {
          setCell(r, C0, item.text, "s", S.menuGrp);
          mergeRange(r, C0, r, C2);
          styleRange(r, C0, r, C2, S.menuGrp);
        } else if (item.type === "colHdr") {
          setCell(r, C0, "Material Name", "s", S.colHdr);
          setCell(r, C1, "Qty", "s", S.colHdr);
          setCell(r, C2, "Unit", "s", S.colHdr);
        } else if (item.type === "dishHdr") {
          setCell(r, C0, item.text, "s", S.dishGrp);
          mergeRange(r, C0, r, C2);
          styleRange(r, C0, r, C2, S.dishGrp);
        } else {
          const odd = item.dataIdx % 2 !== 0;
          setCell(r, C0, item.name, "s", odd ? S.dataOdd : S.dataEven);
          setCell(r, C1, item.qty, "n", S.dataNum(odd));
          setCell(r, C2, item.unit, "s", odd ? S.dataOdd : S.dataEven);
        }
      });
    };

    for (let pairIdx = 0; pairIdx < rawMaterials.length; pairIdx += 2) {
      const leftSeq  = buildMenuSeq(rawMaterials[pairIdx]);
      const rightBlock = rawMaterials[pairIdx + 1];
      const rightSeq = rightBlock ? buildMenuSeq(rightBlock) : [];
      const pairHeight = Math.max(leftSeq.length, rightSeq.length);

      placeSeq(leftSeq, 0, ri);
      if (rightSeq.length > 0) placeSeq(rightSeq, 4, ri);

      // Gap column
      for (let r = ri; r < ri + pairHeight; r++)
        setCell(r, 3, "", "s", S.gap);

      ri += pairHeight;
      if (pairIdx + 2 < rawMaterials.length) ri += 2; // gap between pairs
    }

    // ── Build sheet ───────────────────────────────────────────────────────
    const ws = {
      "!ref": XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxRow, c: MAX_COL } }),
    };
    Object.assign(ws, cells);
    ws["!merges"] = merges;
    ws["!cols"] = [
      { wch: 28 }, { wch: 12 }, { wch: 10 },
      { wch: 3  },
      { wch: 28 }, { wch: 12 }, { wch: 10 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Required Materials");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" }), "Required_Raw_Materials.xlsx");
  };

  const handleConvertPo = () => {
    navigate("/central/create-po", {
      state: {
        rawMaterials: rawMaterials, 
        zone: {desc: user.zone?.desc || null, id: user.zone?._id || null}
      }
    });
  }

  // Fetch Menus from backend
  useEffect(() => {
    console.log(user);
    const fetchMenus = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/menu/getAll`,
          { withCredentials: true }
        );
        console.log(response.data);
        setMenus(response.data.data);
      } catch (error) {
        console.error("Error fetching menus:", error);
        setNotification({ message: "Error fetching menus", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchMenus();
  }, []);

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, navigate, setUser]);

  const handleMenuChange = async (index, menuId) => {
    // Clone selected menus array
    const updatedMenus = [...selectedMenus];

    // Reset state for selected menu index
    updatedMenus[index].id = menuId;
    updatedMenus[index].details = null;
    updatedMenus[index].quantities = {};

    // Clear previous yield applied state for this index (fresh selection)
    setMenuYieldApplied(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    // Reset global UI states
    setNotification({ message: "", type: "" });
    setShowRawMaterials(false);
    setRawMaterials([]);
    setSubmittedFormData([]);

    // Fetch and update menu details
    const selectedMenuObj = menus.find((m) => m._id === menuId);

    if (menuId && selectedMenuObj) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/dish/${menuId}/details`,
          { withCredentials: true }
        );
        const dishData = response.data.data;
        updatedMenus[index].details = dishData;

        const initialQuantities = {};
        dishData.dishes.forEach((dishItem) => {
          initialQuantities[dishItem._id] = "";
        });

        updatedMenus[index].quantities = initialQuantities;

        // Always attempt to fetch existing yield (admin or any role)
        try {
          const yieldResp = await axios.get(`${process.env.REACT_APP_API_URI}/api/menuYield/${menuId}`);
          const yieldData = yieldResp.data?.data;
          if (yieldData?.dishes?.length) {
            yieldData.dishes.forEach(yd => {
              const match = dishData.dishes.find(d => d.dish._id === yd.dish._id || d.dish._id === yd.dish);
              if (match) {
                updatedMenus[index].quantities[match._id] = yd.quantity;
              }
            });
            setMenuYieldApplied(prev => ({ ...prev, [index]: isAdminRole(user?.role) ? 'admin' : 'central' }));
          }
        } catch (yErr) {
          console.log('No yield found or error fetching yield', yErr?.response?.data || yErr.message);
        }
      } catch (err) {
        console.error("Error fetching menu details:", err);
        setNotification({
          message: "Error fetching menu details",
          type: "error",
        });
      } finally {
        setLoadingDetails(false);
      }
    }

    setSelectedMenus(updatedMenus);
  };

  const handleAddMenu = () => {
    setSelectedMenus([
      ...selectedMenus,
      { id: "", details: null, quantities: {} },
    ]);
  };

  const handleRemoveMenu = (index) => {
    const updated = [...selectedMenus];
    updated.splice(index, 1);
    setSelectedMenus(updated);
  };

  const handleQuantityChange = (dishItemId, quantity) => {
    setDishQuantities((prev) => ({
      ...prev,
      [dishItemId]: quantity,
    }));
  };

  // Updated function to handle raw material quantity changes with dish-specific key
  const handleRawMaterialQuantityChange = (menuIdx, dishIdx, materialIdx, quantity) => {
    setRawMaterials(prev => {
      const updated = [...prev];
      updated[menuIdx].dishes[dishIdx].item[materialIdx].quantity = parseFloat(quantity) || 0;
      return updated;
    });
  };

  // Updated function to remove raw material with dish-specific key
  const handleRemoveRawMaterial = (menuIdx, dishIdx, materialIdx) => {
    setRawMaterials(prev => {
      const updated = [...prev];
      updated[menuIdx].dishes[dishIdx].item.splice(materialIdx, 1);
      return updated;
    });
  };

  // Updated function to remove entire dish
const handleRemoveDish = (menuIdx, dishIdx) => {
  setRawMaterials(prev => {
    const updated = [...prev];
    updated[menuIdx].dishes.splice(dishIdx, 1);
    return updated;
  });
};

  // Updated function to search items for specific dish
  const handleSearchItems = async (dishKey, searchValue) => {
    console.log("Search value:", searchValue, "for dish:", dishKey);
    
    setSearchTerms(prev => ({
      ...prev,
      [dishKey]: searchValue
    }));

    try {
      setSearchLoading(prev => ({
        ...prev,
        [dishKey]: true
      }));
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/findItems`,
        {
          params: { q: searchValue },
        }
      );
      
      console.log("Search results:", response.data);
      setSearchResults(prev => ({
        ...prev,
        [dishKey]: response.data
      }));
    } catch (error) {
      console.error("Error searching items:", error);
    } finally {
      setSearchLoading(prev => ({
        ...prev,
        [dishKey]: false
      }));
    }
  };

  // Updated function to select item from search for specific dish
  const handleSelectItem = (dishKey, item) => {
    setSelectedNewItems(prev => ({
      ...prev,
      [dishKey]: item
    }));
    
    setSearchResults(prev => ({
      ...prev,
      [dishKey]: []
    }));
    
    setSearchTerms(prev => ({
      ...prev,
      [dishKey]: item.name
    }));
  };

  // Updated function to add new item to specific dish
  const handleAddNewItemWithQuantity = (menuIdx, dishIdx) => {
    const dishKey = `${menuIdx}-${dishIdx}`;
    const selectedItem = selectedNewItems[dishKey];
    const quantity = newItemQuantities[dishKey];
    
    console.log("Selected new item:", selectedItem);
    console.log("New item quantity:", quantity);
    
    if (!selectedItem) {
      setNotification({
        message: "Please select an item first",
        type: "error",
      });
      return;
    }

    if (!quantity || quantity <= 0) {
      setNotification({
        message: "Please enter a valid quantity",
        type: "error",
      });
      return;
    }

    const newRawMaterial = {
      dish: {
        _id: selectedItem._id,
        name: selectedItem.name,
        unit: selectedItem.unit,
      },
      quantity: parseFloat(quantity),
    };
    
    console.log("New raw material:", newRawMaterial);
    
    // Check if item already exists in this specific dish
    const exists = rawMaterials[menuIdx].dishes[dishIdx].item.some(
      (material) => material.dish._id === selectedItem._id
    );
    
    if (!exists) {
      setRawMaterials(prev => {
        const updated = [...prev];
        updated[menuIdx].dishes[dishIdx].item.push(newRawMaterial);
        return updated;
      });
      
      // Reset add item form for this dish
      setShowAddItemForDish(prev => ({
        ...prev,
        [dishKey]: false
      }));
      
      setSearchTerms(prev => ({
        ...prev,
        [dishKey]: ""
      }));
      
      setSearchResults(prev => ({
        ...prev,
        [dishKey]: []
      }));
      
      setSelectedNewItems(prev => ({
        ...prev,
        [dishKey]: null
      }));
      
      setNewItemQuantities(prev => ({
        ...prev,
        [dishKey]: ""
      }));
      
    } else {
      setNotification({
        message: "Item already exists in this dish",
        type: "error",
      });
    }
  };

  // Updated function to cancel add item for specific dish
  const handleCancelAddItem = (dishKey) => {
    setShowAddItemForDish(prev => ({
      ...prev,
      [dishKey]: false
    }));
    
    setSearchTerms(prev => ({
      ...prev,
      [dishKey]: ""
    }));
    
    setSearchResults(prev => ({
      ...prev,
      [dishKey]: []
    }));
    
    setSelectedNewItems(prev => ({
      ...prev,
      [dishKey]: null
    }));
    
    setNewItemQuantities(prev => ({
      ...prev,
      [dishKey]: ""
    }));
  };

  const handleFinalSubmit = async () => {
    console.log("Raw materials:", rawMaterials);
    
    // Flatten the nested structure to create a flat array of raw materials
    const flattenedRawMaterials = [];
    
    rawMaterials.forEach((menuBlock) => {
        menuBlock.dishes.forEach((dishBlock) => {
            dishBlock.item.forEach((material) => {
                // Check if this item already exists in the flattened array
                const existingItemIndex = flattenedRawMaterials.findIndex(
                    item => item.dish._id === material.dish._id
                );
                
                if (existingItemIndex >= 0) {
                    // If item exists, add to the quantity
                    flattenedRawMaterials[existingItemIndex].quantity += material.quantity;
                } else {
                    // If item doesn't exist, add it to the array
                    flattenedRawMaterials.push({
                        dish: material.dish,
                        quantity: material.quantity
                    });
                }
            });
        });
    });
    
    // Validation
    const hasEmptyQuantities = flattenedRawMaterials.some(
        (material) => !material.quantity || material.quantity <= 0
    );
    
    console.log("Has empty quantities:", hasEmptyQuantities);
    console.log("Flattened raw materials:", flattenedRawMaterials);
    
    if (hasEmptyQuantities) {
        setNotification({
            message: "Please enter valid quantities for all raw materials",
            type: "error",
        });
        return;
    }

    try {
        const finalData = {
            menuId: submittedFormData[0]?.menuId || selectedMenu,
            menuName: submittedFormData[0]?.menuName || menuDetails?.name,
            thaalCount: totalThaal,
            rawMaterials: flattenedRawMaterials,
            dishDetails: submittedFormData.flatMap(menu => menu.dishes),
            user: user,
            menuStructure: rawMaterials
        };

        console.log("Final data being sent:", finalData);

        const response = await axios.post(
            `${process.env.REACT_APP_API_URI}/api/request/menu`,
            finalData,
            { withCredentials: true }
        );

        setNotification({
            message: "Zone Menu Created Successfully",
            type: "success",
        });
        
        // Reset form
        setSelectedMenu("");
        setMenuDetails(null);
        setDishQuantities({});
        setTotalThaal(0);
        setRawMaterials([]);
        setShowRawMaterials(false);
        setSubmittedFormData([]);
        setSelectedMenus([{ id: "", details: null, quantities: {} }]);
        setHasGeneratedRawMaterials(false);
        
        // Reset add item states
        setShowAddItemForDish({});
        setSearchTerms({});
        setSearchResults({});
        setSelectedNewItems({});
        setNewItemQuantities({});
        
    } catch (error) {
        console.error("Error creating zone menu:", error);
        if (error.response && error.response.status === 409) {
            setNotification({
                message: "Zone Menu already exists for this configuration",
                type: "error",
            });
        } else {
            setNotification({
                message: "An error occurred while creating the Zone Menu",
                type: "error",
            });
        }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate dish quantities
    const hasInvalidQuantities = selectedMenus.some((menu) =>
      Object.values(menu.quantities).some((qty) => !qty || qty <= 0)
    );
    if (hasInvalidQuantities) {
      setNotification({
        message: "Please enter valid quantities for all dishes",
        type: "error",
      });
      return;
    }

    if (!totalThaal || totalThaal <= 0) {
      setNotification({
        message: "Please enter valid total thaal count",
        type: "error",
      });
      return;
    }

    const menusPayload = selectedMenus
      .map((menu) => {
        if (!menu.details) return null;

        const dishDetails = menu.details.dishes.map((dishItem) => ({
          dishItemId: dishItem._id,
          dishId: dishItem.dish._id,
          dishName: dishItem.dish.name,
          unit: dishItem.dish.unit,
          quantity: parseFloat(menu.quantities[dishItem._id]) || 0,
          perThaalQuantity: dishItem.perThaalQuantity,
        }));

        return {
          menuId: menu.id,
          menuName: menu.details.name,
          dishes: dishDetails,
        };
      })
      .filter(Boolean);

    const payload = {
      menus: menusPayload,
      thaalCount: totalThaal,
    };

    // Store all menu data for summary display later
    setSubmittedFormData(menusPayload);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/menu/calculateQuantity`,
        payload,
        { withCredentials: true }
      );
      
      const rawMaterialByMenu = response.data.data;
      setNotification({
        message: "Raw Materials Generated Successfully",
        type: "success",
      });
      setRawMaterials(rawMaterialByMenu); 
      setShowRawMaterials(true);
      setHasGeneratedRawMaterials(true);
    } catch (error) {
      console.error("Error generating raw materials:", error);
      setNotification({
        message: "An error occurred while generating raw materials",
        type: "error",
      });
    }
  };

  // Build yield payload from currently selected (first) menu before generation
  const handleSaveMenuYield = async (menuIndex) => {
    if (hasGeneratedRawMaterials) return; // not after generation
    if (!isAdminRole(user?.role)) return;

    const targetMenu = selectedMenus[menuIndex];
    if (!targetMenu?.id || !targetMenu?.details) {
      setNotification({ message: 'Select a menu first', type: 'error' });
      return;
    }

    const dishEntries = targetMenu.details.dishes.map(dishItem => ({
      dishId: dishItem.dish._id,
      quantity: parseFloat(targetMenu.quantities[dishItem._id]) || 0
    }));

    const invalid = dishEntries.some(d => !d.quantity || d.quantity <= 0);
    if (invalid) {
      setNotification({ message: 'Enter valid quantities for all dishes before saving yield', type: 'error' });
      return;
    }

  const payload = { menuId: targetMenu.id, dishes: dishEntries, source: 'Central Admin' };

    try {
      await axios.post(`${process.env.REACT_APP_API_URI}/api/menuYield/save`, payload, { withCredentials: true });
      setMenuYieldSaveStatus(prev => ({ ...prev, [menuIndex]: 'success' }));
      setMenuYieldApplied(prev => ({ ...prev, [menuIndex]: 'admin' }));
    } catch (error) {
      console.error('Error saving menu yield', error);
      setMenuYieldSaveStatus(prev => ({ ...prev, [menuIndex]: 'error' }));
    }
  };

  return (
    <>
      <div className="wrapper">
        <div className="w-full">
          <h2 className="text-base font-bold bg-lime-800 text-white p-3 py-4 text-center">
            Zone Menu Creation{" "}
            {isAdminRole(user?.role) && user?.zone ? "- " + user.zone.desc : null}
          </h2>
          {notification.message && (
            <div
              className={`p-2 mb-4 ${
                notification.type === "error"
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
              }`}
            >
              {notification.message}
            </div>
          )}
        </div>

        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="px-8 pt-6 pb-8 mb-4">
            {/* Menu Selection */}
            {!hasGeneratedRawMaterials && (
              <div className="mb-6">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="menu-select"
                >
                  Select Menu
                </label>
              </div>
            )}

            {/* Loading Details */}
            {loadingDetails && (
              <div className="text-center py-4">
                <p className="text-gray-600">Loading menu details...</p>
              </div>
            )}

            {/* Menu Details Form */}
            {!hasGeneratedRawMaterials &&
              selectedMenus.map((menu, index) => (
                <div key={index} className="mb-6 border p-4 rounded-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <select
                      value={menu.id}
                      onChange={(e) => handleMenuChange(index, e.target.value)}
                      className="shadow border rounded px-3 py-2 w-full"
                    >
                      <option value="">Choose a menu</option>
                      {menus
                        .filter(
                          (m) =>
                            !selectedMenus.some(
                              (sm, i) => sm.id === m._id && i !== index
                            )
                        )
                        .map((m) => (
                          <option key={m._id} value={m._id}>
                            {m.name}
                          </option>
                        ))}
                    </select>

                    {selectedMenus.length > 1 && (
                      <button
                        onClick={() => handleRemoveMenu(index)}
                        type="button"
                        className="bg-red-500 text-white px-3 py-1 rounded"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {menuYieldApplied[index] && (
                    <div className="mb-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded">
                      {menuYieldApplied[index] === 'admin' ? 'Previously saved yield loaded' : 'Standard yield applied (From Central Admin)'}
                    </div>
                  )}
                  {menuYieldSaveStatus[index] === 'success' && (
                    <div className="mb-2 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded">
                      Menu Yield saved successfully
                    </div>
                  )}
                  {menuYieldSaveStatus[index] === 'error' && (
                    <div className="mb-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">
                      Failed to save Menu Yield
                    </div>
                  )}

                  {menu.details &&
                    menu.details.dishes.map((dish, i) => (
                      <div
                        key={dish._id}
                        className="grid grid-cols-3 gap-4 mb-3"
                      >
                        <input
                          readOnly
                          value={dish.dish.name}
                          className="bg-gray-100 border px-2 py-1 rounded"
                        />
                        <input
                          type="number"
                          placeholder="Qty"
                          value={menu.quantities[dish._id] || ""}
                          onChange={(e) => {
                            const updated = [...selectedMenus];
                            updated[index].quantities[dish._id] =
                              e.target.value;
                            setSelectedMenus(updated);
                          }}
                          onWheel={(e) => e.target.blur()}
                          className="border px-2 py-1 rounded"
                        />
                        <input
                          readOnly
                          value={dish.dish.unit}
                          className="bg-gray-100 border px-2 py-1 rounded"
                        />
                      </div>
                    ))}

                  {/* Save Menu Yield button after dish list for every menu (Admin only) */}
                  {!hasGeneratedRawMaterials && isAdminRole(user?.role) && menu.details && (
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleSaveMenuYield(index)}
                        className="bg-blue-600 hover:bg-blue-800 text-white font-semibold py-2 px-5 rounded-full text-sm transition-colors"
                      >
                        Save Menu Yield
                      </button>
                    </div>
                  )}
                </div>
              ))}

            {!hasGeneratedRawMaterials && (
              <button
                type="button"
                onClick={handleAddMenu}
                className="bg-green-600 text-white px-4 py-2 rounded mb-6"
              >
                + Add Menu
              </button>
            )}
            
            {!hasGeneratedRawMaterials && (
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Total Thaal *
                </label>
                <input
                  type="number"
                  value={totalThaal}
                  onChange={(e) => setTotalThaal(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                  min="1"
                />
              </div>
            )}
            
            {/* Submit Button */}
            {!hasGeneratedRawMaterials && (
              <div className="flex items-center justify-center mt-8">
                <div className="flex flex-col gap-4 w-full items-center">
                  <button
                    type="submit"
                    className="bg-fmb text-white font-bold py-2 px-6 rounded-full focus:outline-none focus:shadow-outline hover:bg-opacity-90 transition-colors"
                  >
                    Generate Required Raw Materials
                  </button>
                </div>
              </div>
            )}

            {/* Raw Materials Management Section */}
            {showRawMaterials && submittedFormData && (
              <>
                <div className="mt-6">
                  {/* Summary of Submitted Data */}
                  <div className="mb-6 p-4 border border-blue-300 rounded-lg bg-blue-50">
                    <h3 className="text-lg font-bold text-blue-800 mb-3">
                      Order Summary
                    </h3>
                    {submittedFormData.map((menu, idx) => (
                      <div key={idx} className="mb-4 border-b pb-2">
                        <p>
                          <span className="font-semibold">Menu:</span>{" "}
                          {menu.menuName}
                        </p>
                        <p>
                          <span className="font-semibold">Dishes:</span>
                        </p>
                        <ul className="ml-4 list-disc">
                          {menu.dishes.map((dish, index) => (
                            <li key={index}>
                              {dish.dishName} - {n2(dish.quantity)} {dish.unit} (Per
                              Thaal: {n2(dish.perThaalQuantity)})
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <p className="mt-2">
                      <span className="font-semibold">Total Thaal:</span>{" "}
                      {totalThaal}
                    </p>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800 border-b pb-2">
                      Required Raw Materials
                    </h3>

                    <div>
                      <button
                        type="button"
                        onClick={handleExportToExcel}
                        className="bg-blue-600 hover:bg-blue-800 text-white px-4 py-2 rounded shadow"
                      >
                        Export  Excel
                      </button>
                      
                      <button
                        type="button"
                        onClick={handleExportConsolidatedExcel}
                        className="bg-purple-600 hover:bg-purple-800 text-white px-4 py-2 rounded shadow ml-2"
                      >
                        Export Consolidated Excel
                      </button>
                    </div>


                  </div>
                  

                  {/* Raw Materials List */}
                  {rawMaterials.map((menuBlock, mIdx) => (
                    <div key={mIdx} className="mb-6 border border-gray-300 rounded-lg p-4 bg-gray-50">
                      <h4 className="text-lg font-bold text-gray-800 mb-2">{menuBlock.menu}</h4>

                      {menuBlock.dishes.map((dishBlock, dIdx) => (
                        <div key={dIdx} className="mb-4 pl-4">
                          <div className="flex justify-between mb-5">
                            <h5 className="text-md font-semibold text-gray-700">
                              Dish: {dishBlock.dish}
                            </h5>
                            <button
                              type="button"
                              onClick={() => handleRemoveDish(mIdx, dIdx)}
                              className="bg-red-700 hover:bg-red-900 text-white px-3 py-1 rounded text-sm"
                            >
                              Remove Dish
                            </button>
                          </div>

                          {dishBlock.item.map((material, materialIdx) => (
                            <div
                              key={material.dish._id}
                              className="mb-3 p-3 border border-gray-200 rounded-lg bg-white"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                                {/* Material Name */}
                                <div>
                                  <label className="block text-gray-700 text-sm font-bold mb-1">
                                    Material Name
                                  </label>
                                  <input
                                    type="text"
                                    value={material.dish.name}
                                    readOnly
                                    className="bg-gray-100 border rounded w-full py-2 px-3 text-sm"
                                  />
                                </div>

                                {/* Quantity */}
                                <div>
                                  <label className="block text-gray-700 text-sm font-bold mb-1">
                                    Quantity *
                                  </label>
                                  <input
                                    type="number"
                                    value={parseFloat(material.quantity).toFixed(2)}
                                    onChange={(e) => handleRawMaterialQuantityChange(
                                      mIdx,
                                      dIdx,
                                      materialIdx,
                                      e.target.value
                                    )}
                                    className="border rounded w-full py-2 px-3 text-sm"
                                    step="0.01"
                                    min="0"
                                    onWheel={(e) => e.target.blur()}
                                  />
                                </div>

                                {/* Unit */}
                                <div>
                                  <label className="block text-gray-700 text-sm font-bold mb-1">
                                    Unit
                                  </label>
                                  <input
                                    type="text"
                                    value={material.dish.unit}
                                    readOnly
                                    className="bg-gray-100 border rounded w-full py-2 px-3 text-sm"
                                  />
                                </div>

                                {/* Spacer for alignment */}
                                <div></div>

                                {/* Remove Button */}
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveRawMaterial(mIdx, dIdx, materialIdx)}
                                    className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Add Item Section for Individual Dish */}
                          <div className="mt-3">
                            {!showAddItemForDish[`${mIdx}-${dIdx}`] ? (
                              <button
                                type="button"
                                onClick={() => setShowAddItemForDish(prev => ({
                                  ...prev,
                                  [`${mIdx}-${dIdx}`]: true
                                }))}
                                className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm"
                              >
                                + Add Item to {dishBlock.dish}
                              </button>
                            ) : (
                              <div className="p-3 border border-green-300 rounded-lg bg-green-50">
                                <h6 className="text-sm font-semibold text-green-700 mb-2">
                                  Add New Item to {dishBlock.dish}
                                </h6>

                                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                                  {/* Item Name with Search */}
                                  <div className="relative">
                                    <label className="block text-gray-700 text-xs font-bold mb-1">
                                      Item Name *
                                    </label>
                                    <input
                                      type="text"
                                      value={searchTerms[`${mIdx}-${dIdx}`] || ""}
                                      onChange={(e) => handleSearchItems(`${mIdx}-${dIdx}`, e.target.value)}
                                      placeholder="Type to search items..."
                                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-sm"
                                    />
                                    {searchLoading[`${mIdx}-${dIdx}`] && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        Searching...
                                      </p>
                                    )}

                                    {/* Search Results Dropdown */}
                                    {searchResults[`${mIdx}-${dIdx}`]?.length > 0 && (
                                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                                        {searchResults[`${mIdx}-${dIdx}`].map((item) => (
                                          <div
                                            key={item._id}
                                            className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 text-sm"
                                            onClick={() => handleSelectItem(`${mIdx}-${dIdx}`, item)}
                                          >
                                            <span className="font-medium">
                                              {item.name}
                                            </span>
                                            <span className="text-xs text-gray-600 ml-2">
                                              ({item.unit})
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Quantity */}
                                  <div>
                                    <label className="block text-gray-700 text-xs font-bold mb-1">
                                      Quantity *
                                    </label>
                                    <input
                                      type="number"
                                      value={newItemQuantities[`${mIdx}-${dIdx}`] || ""}
                                      onChange={(e) => setNewItemQuantities(prev => ({
                                        ...prev,
                                        [`${mIdx}-${dIdx}`]: e.target.value
                                      }))}
                                      placeholder="Enter quantity"
                                      min="0"
                                      step="0.01"
                                      onWheel={(e) => e.target.blur()}
                                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-sm"
                                    />
                                  </div>

                                  {/* Unit */}
                                  <div>
                                    <label className="block text-gray-700 text-xs font-bold mb-1">
                                      Unit
                                    </label>
                                    <input
                                      type="text"
                                      value={selectedNewItems[`${mIdx}-${dIdx}`] ? selectedNewItems[`${mIdx}-${dIdx}`].unit : ""}
                                      readOnly
                                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight bg-gray-100 text-sm"
                                      placeholder="Select item first"
                                    />
                                  </div>

                                  {/* Spacer */}
                                  <div></div>

                                  {/* Action Buttons */}
                                  <div className="flex gap-2 justify-center">
                                    <button
                                      type="button"
                                      onClick={() => handleAddNewItemWithQuantity(mIdx, dIdx)}
                                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline text-xs"
                                    >
                                      Add
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleCancelAddItem(`${mIdx}-${dIdx}`)}
                                      className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline text-xs"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}

                  {user.role === "CityAdmin" || user.role === "SuperAdmin" ? (
                    <>
                      <button
                        type="button"
                        onClick={handleExportToExcel}
                        className="bg-blue-600 hover:bg-blue-800 text-white px-20 py-2 rounded shadow ml-[50%] -translate-x-1/2"
                      >
                        Export to Excel
                      </button>
                      <button
                        type="button"
                        onClick={handleExportConsolidatedExcel}
                        className="bg-purple-600 hover:bg-purple-800 text-white px-20 py-2 rounded shadow ml-[50%] -translate-x-1/2 mt-5"
                      >
                        Export Consolidated Excel
                      </button>
                      <button
                        type="button"
                        onClick={handleConvertPo}
                        className="bg-green-600 hover:bg-green-800 text-white px-20 py-2 rounded shadow ml-[50%] -translate-x-1/2 mt-5"
                      >
                        Convert to PO
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center justify-center mt-8">
                      <button
                        type="button"
                        onClick={handleFinalSubmit}
                        className="bg-fmb text-white font-bold py-2 px-6 rounded-full focus:outline-none focus:shadow-outline hover:bg-opacity-90 transition-colors"
                      >
                        Create Request
                      </button>
                    </div>
                  )}
                  {/* Final Submit Button */}

                </div>
              </>
            )}

            {/* No Dishes Found */}
            {selectedMenu &&
              !loadingDetails &&
              menuDetails &&
              menuDetails.dishes.length === 0 && (
                <div className="mt-6 p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
                  <p className="text-yellow-800">
                    No dishes found for this menu.
                  </p>
                </div>
              )}
          </form>
        </div>
      </div>
    </>
  );
};

export default ZoneMenuCreation;